/**
 * Parse an input into a finite number, or return null if not a number.
 *
 * Accepts the already-separated form the formatter produces (``1,234.56``)
 * by stripping commas before parsing. Empty / non-numeric inputs return
 * null so callers can decide to render them as plain strings.
 */
export function parseNumeric(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    const cleaned = trimmed.replace(/,/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Format a number with locale thousands separators, preserving up to 10
 * fractional digits so we don't silently truncate precision. Returns the
 * original string if the value isn't a finite number.
 */
export function formatWithThousands(value: unknown): string {
  const n = parseNumeric(value);
  if (n === null) {
    return value === null || value === undefined ? "" : String(value);
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 10 });
}
